import React from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import translations from './translations';
import useArrayField from '@afosto/app-components-react/ArrayField/useArrayField';
import useRegisterInput from '@afosto/app-components-react/Form/useRegisterInput';
import { useFormContext } from '@afosto/app-components-react/Form';
import Box from '@material-ui/core/Box';
import CardActions from '@material-ui/core/CardActions';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import Select from '@afosto/app-components-react/Select';
import CountrySelect from '@afosto/app-components-react/CountrySelect';
import findObjectInArray from '@afosto/utils/array/findObjectInArray';
import VatRateSearch from '@afosto/app-components-react/VatRateSearch';

const VAT_SETTING_STRUCTURE = {
  type: 'fixed',
  reference: null,
  countryCode: 'NL',
};

const VatSettingsFormSection = ({ countries }) => {
  const name = 'vatSettings';
  useRegisterInput(name);

  const intl = useIntl();
  const formContext = useFormContext();
  const { watch, errors } = formContext ?? {};
  const inputValue = watch(name);

  const { data, append, remove, update } = useArrayField({
    name,
    value: !inputValue?.length
      ? [
          {
            ...VAT_SETTING_STRUCTURE,
            countryCode: findObjectInArray(countries, 'value', VAT_SETTING_STRUCTURE?.countryCode),
          },
        ]
      : inputValue,
  });

  const handleChange = (index, field) => (ev, newValue) => {
    update(index, field)(ev, newValue);
  };

  return (
    <Card style={{ marginBottom: 8 }}>
      <CardHeader
        title={intl.formatMessage(translations.title)}
        titleTypographyProps={{ variant: 'h6' }}
      />
      <CardContent>
        <FormHelperText error={errors?.vatSettings?.type} hidden={!errors.vatSettings?.type}>
          {intl.formatMessage(
            {
              id: `errors.validation.${errors?.vatSettings?.type}`,
              defaultMessage: errors?.vatSettings?.message,
              description: `Error message for ${name} with type ${errors?.vatSettings?.type} and message ${errors?.vatSettings?.errorMessage}`,
            },
            { target: intl.formatMessage(translations.title) },
          )}
        </FormHelperText>
        <FormControl component="fieldset" variant="outlined" fullWidth>
          {data.map((settings, idx) => (
            // eslint-disable-next-line no-underscore-dangle
            <Box py={1} key={settings._id}>
              <Card>
                <CardContent>
                  <Select
                    name="type"
                    label={intl.formatMessage(translations.fields.type)}
                    suggestions={[
                      {
                        label: intl.formatMessage(translations.vatTypes.fixed),
                        value: 'fixed',
                      },
                      {
                        label: intl.formatMessage(translations.vatTypes.max),
                        value: 'max',
                      },
                      {
                        label: intl.formatMessage(translations.vatTypes.weighed),
                        value: 'weighed',
                      },
                    ]}
                    onChange={handleChange(idx, 'type')}
                    value={settings.type}
                    register={false}
                    error={errors?.vatSettings?.[idx]?.type}
                    errorMessage={intl.formatMessage(
                      {
                        id: `errors.validation.${errors?.vatSettings?.[idx]?.type?.type}`,
                        defaultMessage: errors?.vatSettings?.[idx]?.type?.message,
                        description: `Error message for ${name} with type ${errors?.vatSettings?.[idx]?.type?.type} and message ${errors?.vatSettings?.[idx]?.type?.errorMessage}`,
                      },
                      { target: intl.formatMessage(translations.fields.type) },
                    )}
                  />
                  <CountrySelect
                    label={intl.formatMessage(translations.fields.countryCode)}
                    name="countryCode"
                    value={settings?.countryCode ?? null}
                    register={false}
                    options={countries}
                    onChange={handleChange(idx, 'countryCode')}
                    error={errors?.vatSettings?.[idx]?.countryCode}
                    errorMessage={intl.formatMessage(
                      {
                        id: `errors.validation.${errors?.vatSettings?.[idx]?.countryCode?.type}`,
                        defaultMessage: errors?.vatSettings?.[idx]?.countryCode?.message,
                        description: `Error message for ${name} with type ${errors?.vatSettings?.[idx]?.countryCode?.type} and message ${errors?.vatSettings?.[idx]?.countryCode?.errorMessage}`,
                      },
                      { target: intl.formatMessage(translations.fields.countryCode) },
                    )}
                  />
                  <VatRateSearch
                    name="reference"
                    label={intl.formatMessage(translations.fields.rate)}
                    onChange={handleChange(idx, 'reference')}
                    value={settings.reference ?? {}}
                    register={false}
                    countryCode={settings?.countryCode?.value ?? 'NL'}
                    error={errors?.vatSettings?.[idx]?.reference}
                    errorMessage={intl.formatMessage(
                      {
                        id: `errors.validation.${errors?.vatSettings?.[idx]?.reference?.type}`,
                        defaultMessage: errors?.vatSettings?.[idx]?.reference?.message,
                        description: `Error message for ${name} with type ${errors?.vatSettings?.[idx]?.reference?.type} and message ${errors?.vatSettings?.[idx]?.reference?.errorMessage}`,
                      },
                      { target: intl.formatMessage(translations.fields.rate) },
                    )}
                  />
                </CardContent>
                <CardActions>
                  <Box marginLeft="auto" px={1} pb={1}>
                    <Button
                      color="secondary"
                      variant="outlined"
                      size="small"
                      onClick={() => remove(idx)}
                      disabled={inputValue?.length <= 1}
                    >
                      {intl.formatMessage(translations.remove)}
                    </Button>
                  </Box>
                </CardActions>
              </Card>
            </Box>
          ))}
          <Button
            color="primary"
            variant="outlined"
            size="small"
            onClick={() =>
              append({
                ...VAT_SETTING_STRUCTURE,
                countryCode: findObjectInArray(
                  countries,
                  'value',
                  VAT_SETTING_STRUCTURE?.countryCode,
                ),
              })
            }
          >
            {intl.formatMessage(translations.add)}
          </Button>
        </FormControl>
      </CardContent>
    </Card>
  );
};

VatSettingsFormSection.propTypes = {
  countries: PropTypes.array,
};

VatSettingsFormSection.defaultProps = {
  countries: [],
};

export default VatSettingsFormSection;
